<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title" v-if="batch">Lot #{{ batch.no }}</h1>
        <h1 class="page-title" v-else>Chargement...</h1>
      </div>
      <v-btn color="primary" outlined small @click="exportPDF" v-if="batch">
        <v-icon left small>mdi-file-pdf-box</v-icon> PDF
      </v-btn>
    </div>

    <!-- Loading -->
    <div v-if="!enable" class="page-loader">
      <v-progress-circular indeterminate color="primary" size="40" width="3"></v-progress-circular>
    </div>

    <div v-if="enable && batch">
      <!-- Info cards -->
      <v-row class="mb-2">
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Numéro</div>
            <div class="info-value">#{{ batch.no }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Type</div>
            <div class="info-value">
              <v-chip small :color="batchTypeColor" dark>{{ batchTypeLabel }}</v-chip>
            </div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Date</div>
            <div class="info-value">{{ batch.created_at | formatDate }}</div>
            <div class="info-sub">{{ batch.created_at | formatTime }}</div>
          </div>
        </v-col>
        <v-col cols="6" sm="3">
          <div class="info-item">
            <div class="info-label">Produits</div>
            <div class="info-value">{{ batch.operations.length }}</div>
          </div>
        </v-col>
      </v-row>

      <!-- Memo -->
      <v-card flat class="detail-card mb-4" v-if="batch.memo">
        <v-card-title class="detail-card-title">
          <v-icon color="info" size="18" class="mr-2">mdi-note-text-outline</v-icon>
          Mémo
        </v-card-title>
        <v-card-text class="text-body-2">{{ batch.memo }}</v-card-text>
      </v-card>

      <!-- Products table -->
      <v-card flat class="detail-card">
        <v-card-title class="detail-card-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-package-variant</v-icon>
          Produits du lot
          <v-spacer></v-spacer>
          <v-chip small outlined color="primary">{{ batch.operations.length }} article{{ batch.operations.length > 1 ? 's' : '' }}</v-chip>
        </v-card-title>
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">#</th>
              <th class="text-left">Produit</th>
              <th class="text-center">Quantité</th>
              <th class="text-right">Coût unit.</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(op, idx) in batch.operations" :key="op.id">
              <td class="text-caption text--secondary">{{ idx + 1 }}</td>
              <td class="font-weight-medium">{{ op.product ? op.product.name : '—' }}</td>
              <td class="text-center">
                <v-chip x-small color="primary" dark>{{ op.quantity }}</v-chip>
              </td>
              <td class="text-right">{{ fmt(op.cost) }}</td>
              <td class="text-right font-weight-bold">{{ fmt(op.quantity * op.cost) }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <!-- Totals -->
        <div class="totals-section">
          <div class="total-row">
            <span>Total quantité</span>
            <span class="font-weight-bold">{{ totalQty }}</span>
          </div>
          <v-divider class="my-2"></v-divider>
          <div class="total-row total-row--grand">
            <span>Total coût</span>
            <span>{{ fmt(totalCost) }}</span>
          </div>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'

export default {
  name: 'BatchDetailsView',
  components: { BreadcrumbComponent },
  data: () => ({
    items: [
      { text: 'Inventaire', disabled: false, href: '/batches' },
      { text: 'Détails', disabled: true, href: '#' }
    ],
    enable: false,
    batch: null,
  }),

  computed: {
    batchTypeLabel() {
      if (!this.batch || !this.batch.operations || this.batch.operations.length === 0) return '—'
      var t = this.batch.operations[0].type
      return t == 1 ? 'Réception' : (t == 2 ? 'Ajustement' : 'Autre')
    },
    batchTypeColor() {
      if (!this.batch || !this.batch.operations || this.batch.operations.length === 0) return 'grey'
      var t = this.batch.operations[0].type
      return t == 1 ? 'success' : (t == 2 ? 'info' : 'warning')
    },
    totalQty() {
      if (!this.batch) return 0
      return this.batch.operations.reduce(function (s, o) { return s + (parseFloat(o.quantity) || 0) }, 0)
    },
    totalCost() {
      if (!this.batch) return 0
      return this.batch.operations.reduce(function (s, o) { return s + ((parseFloat(o.quantity) || 0) * (parseFloat(o.cost) || 0)) }, 0)
    },
  },

  methods: {
    fmt(v) {
      if (!v && v !== 0) return '0.00'
      var p = Math.abs(parseFloat(v)).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return p.join('.')
    },

    load() {
      var self = this
      this.$axios.get('/batches/' + this.$route.params.id + '?include=operations.product')
        .then(function (response) {
          self.batch = response.data
          self.enable = true
          self.items = [
            { text: 'Inventaire', disabled: false, href: '/batches' },
            { text: '#' + response.data.no, disabled: true, href: '#' }
          ]
        })
    },

    async exportPDF() {
      var self = this
      var b = this.batch
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('p', 'mm', 'a4')
      var ml = 16, mr = 194, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], light = [248, 250, 252], border = [220, 225, 230]

      // Header
      doc.setFillColor(...primary); doc.rect(0, 0, 210, 24, 'F')
      doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('LOT #' + b.no, ml, 12)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      var fDate = new Date(b.created_at).toLocaleDateString('fr-FR')
      var fTime = new Date(b.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      doc.text(fDate + ' à ' + fTime, mr, 9, { align: 'right' })
      if (biz.name) doc.text(biz.name, mr, 15, { align: 'right' })
      doc.setFontSize(7); doc.setTextColor(160, 185, 215)
      doc.text(self.batchTypeLabel.toUpperCase(), ml, 20)

      var y = 32

      // Memo
      if (b.memo) {
        doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...muted)
        doc.text('MÉMO', ml, y); y += 4
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)
        var lines = doc.splitTextToSize(b.memo, cw)
        doc.text(lines, ml, y); y += lines.length * 4 + 6
      }

      // Table
      doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...dark)
      doc.text('Produits', ml, y); y += 5

      var cols = [
        { label: '#', w: 12, align: 'center' },
        { label: 'PRODUIT', w: 80, align: 'left' },
        { label: 'QTÉ', w: 20, align: 'center' },
        { label: 'COÛT UNIT.', w: 28, align: 'right' },
        { label: 'TOTAL', w: 30, align: 'right' },
      ]
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 7

      doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
      doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      cols.forEach(function (c) {
        var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
        doc.text(c.label, tx, y + 5, { align: c.align })
      })
      y += rowH + 1

      doc.setFontSize(8)
      b.operations.forEach(function (op, idx) {
        if (y + rowH > 265) { doc.addPage(); y = 16 }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)
        var name = op.product ? op.product.name : '—'; if (name.length > 40) name = name.substring(0, 38) + '..'
        var vals = [String(idx + 1), name, String(op.quantity), self.fmt(op.cost), self.fmt(op.quantity * op.cost)]
        cols.forEach(function (c, i) {
          var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3)
          if (i === 0) doc.setTextColor(...muted); else doc.setTextColor(...dark)
          doc.text(vals[i], tx, y + 4.5, { align: c.align })
        })
        y += rowH
      })

      // Totals
      y += 2
      doc.setFillColor(...light); doc.rect(ml, y, cw, 10, 'F')
      doc.setDrawColor(...primary); doc.setLineWidth(0.4); doc.line(ml, y, mr, y)
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary)
      doc.text('TOTAL', ml + 3, y + 6.5)
      doc.text(String(self.totalQty), cols[2].x + cols[2].w / 2, y + 6.5, { align: 'center' })
      doc.text(self.fmt(self.totalCost), mr - 3, y + 6.5, { align: 'right' })

      // Footer
      var ph = doc.internal.pageSize.getHeight()
      var totalPages = doc.internal.getNumberOfPages()
      for (var p = 1; p <= totalPages; p++) {
        doc.setPage(p)
        doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 12, mr, ph - 12)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 7)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted); doc.text('Page ' + p + ' / ' + totalPages, 105, ph - 7, { align: 'center' })
        var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 7, { align: 'right' })
      }

      doc.save('lot_' + b.no + '.pdf')
    },
  },

  mounted() { this.load() },
}
</script>

<style scoped>
.info-item { background: #fff; border: 1px solid #e8ecf1; border-radius: 8px; padding: 14px 16px; height: 100%; }
.info-label { font-size: 0.6875rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8; margin-bottom: 4px; }
.info-value { font-size: 0.9375rem; font-weight: 600; color: #1a2332; line-height: 1.3; }
.info-sub { font-size: 0.75rem; color: #64748b; margin-top: 2px; }

.detail-card { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.detail-card-title { font-size: 0.875rem !important; font-weight: 600 !important; padding: 14px 16px !important; color: #1a2332; border-bottom: 1px solid #f1f5f9; }

.detail-card .v-simple-table { border: none !important; box-shadow: none !important; }
.detail-card .v-simple-table thead th { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8 !important; background: transparent !important; border-bottom: 1px solid #e8ecf1 !important; padding: 8px 16px !important; }
.detail-card .v-simple-table tbody td { font-size: 0.8125rem; padding: 12px 16px !important; border-bottom: 1px solid #f1f5f9 !important; }
.detail-card .v-simple-table tbody tr:last-child td { border-bottom: none !important; }
.detail-card .v-simple-table tbody tr:hover { background: rgba(25, 65, 115, 0.02) !important; }

.totals-section { padding: 16px 16px 20px; background: #f8fafc; border-top: 1px solid #e8ecf1; }
.total-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; font-size: 0.875rem; color: #475569; }
.total-row--grand { font-size: 1.125rem; font-weight: 700; color: #194173; padding: 6px 0; }
</style>
